import React from 'react';
import { GraduationCap, ScrollText } from 'lucide-react';
import { EducationCard } from '../ui/EducationCard';
import { CertificationCard } from '../ui/CertificationCard';
import RevealOnScroll from '../../hooks/useRevealOnScroll';
import { education, certifications } from '../../data/portfolioData';
import { useTranslation } from '../../hooks/useTranslation';

export const EducationSection = () => {
    const { t } = useTranslation();

    return (
        <section id="education" className="py-20 px-6 bg-[#0d0d0d] relative">
            <div className="container mx-auto">

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Education */}
                    <div>
                        <RevealOnScroll>
                            <div className="flex items-center gap-3 mb-8">
                                <GraduationCap className="w-8 h-8 text-[#d92323]" />
                                <h2 className="text-3xl font-bold text-white">{t('educationTitle')}</h2>
                            </div>
                        </RevealOnScroll>
                        <div className="space-y-6">
                            {education.map((edu, index) => (
                                <RevealOnScroll key={index} delay={index * 100}>
                                    <EducationCard {...edu} />
                                </RevealOnScroll>
                            ))}
                        </div>
                    </div>

                    {/* Certifications */}
                    <div>
                        <RevealOnScroll>
                            <div className="flex items-center gap-3 mb-8">
                                <ScrollText className="w-8 h-8 text-[#d92323]" />
                                <h2 className="text-3xl font-bold text-white">{t('certificationsTitle')}</h2>
                            </div>
                        </RevealOnScroll>
                        <div className="max-h-[600px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                            {(() => {
                                // Group certifications by issuer
                                const groupedCertifications = certifications.reduce((groups, cert) => {
                                    const issuer = cert.issuer;
                                    if (!groups[issuer]) {
                                        groups[issuer] = [];
                                    }
                                    groups[issuer].push(cert);
                                    return groups;
                                }, {});

                                // Sort groups by issuer name and render
                                return Object.keys(groupedCertifications)
                                    .sort()
                                    .map((issuer, groupIndex) => (
                                        <div key={issuer} className="space-y-3">
                                            <RevealOnScroll delay={groupIndex * 100}>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="w-6 h-6 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0">
                                                        <img
                                                            src={groupedCertifications[issuer][0].logo}
                                                            alt={`${issuer} logo`}
                                                            className="w-full h-full object-contain"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'block';
                                                            }}
                                                        />
                                                        <div className="w-full h-full bg-gradient-to-br from-[#d92323] to-[#b91c1c] flex items-center justify-center text-white text-xs font-bold hidden">
                                                            {issuer.charAt(0)}
                                                        </div>
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-white">{issuer}</h3>
                                                    <div className="flex-1 h-px bg-gradient-to-r from-[#732424] to-transparent"></div>
                                                    <span className="text-sm text-gray-400">
                                                        {groupedCertifications[issuer].length} certificate{groupedCertifications[issuer].length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </RevealOnScroll>
                                            <div className="grid gap-3 ml-9">
                                                {groupedCertifications[issuer].map((cert, certIndex) => (
                                                    <RevealOnScroll key={certIndex} delay={groupIndex * 100 + certIndex * 50}>
                                                        <CertificationCard {...cert} />
                                                    </RevealOnScroll>
                                                ))}
                                            </div>
                                        </div>
                                    ));
                            })()}
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};